import { useState } from 'react';
import type { SyncJob, SyncHistoryEntry } from '@/types';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/utils/cn';
import {
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Eye,
  RotateCcw,
} from 'lucide-react';
import { SyncDetailModal } from './SyncDetailModal';

interface SyncHistoryProps {
  /** Legacy SyncJob format (from queue status) */
  jobs?: SyncJob[];
  /** New SyncHistoryEntry format (from sync_history API) */
  history?: SyncHistoryEntry[];
  /** Callback when retry is requested */
  onRetry?: (syncHistoryId: string) => void;
}

/**
 * SyncHistory component
 *
 * Displays sync history in a list format. Supports both:
 * - Legacy SyncJob format (from queue status)
 * - New SyncHistoryEntry format (from sync_history API)
 */
export function SyncHistory({ jobs, history, onRetry }: SyncHistoryProps) {
  const [selectedSyncId, setSelectedSyncId] = useState<string | null>(null);

  // Use history if available, otherwise fall back to jobs
  const hasHistory = history && history.length > 0;
  const hasJobs = jobs && jobs.length > 0;

  if (!hasHistory && !hasJobs) {
    return (
      <div className="text-center py-12">
        <Clock className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          No sync history
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Start your first sync to see the history here
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {/* Render new history format if available */}
        {hasHistory && history!.map((item) => (
          <SyncHistoryCard
            key={item.id}
            item={item}
            onViewDetails={() => setSelectedSyncId(item.id)}
            onRetry={onRetry ? () => onRetry(item.id) : undefined}
          />
        ))}

        {/* Fall back to legacy format if no history */}
        {!hasHistory && hasJobs && jobs!.map((job) => (
          <SyncJobCard key={job.id} job={job} />
        ))}
      </div>

      {/* Sync detail modal - shows both successful items and errors */}
      {selectedSyncId && (
        <SyncDetailModal
          syncHistoryId={selectedSyncId}
          onClose={() => setSelectedSyncId(null)}
          onRetry={onRetry}
        />
      )}
    </>
  );
}

/**
 * Card for displaying a SyncHistoryEntry (new format)
 */
function SyncHistoryCard({
  item,
  onViewDetails,
  onRetry,
}: {
  item: SyncHistoryEntry;
  onViewDetails: () => void;
  onRetry?: () => void;
}) {
  const getStatusIcon = () => {
    switch (item.status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'partial_success':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'in_progress':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-gray-400" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-gray-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = () => {
    switch (item.status) {
      case 'completed':
        return 'bg-green-50 border-green-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      case 'partial_success':
        return 'bg-yellow-50 border-yellow-200';
      case 'in_progress':
        return 'bg-blue-50 border-blue-200';
      case 'pending':
        return 'bg-gray-50 border-gray-200';
      case 'cancelled':
        return 'bg-gray-50 border-gray-200';
      default:
        return 'bg-yellow-50 border-yellow-200';
    }
  };

  const getSyncTypeLabel = () => {
    const isFullSync = item.triggerMetadata?.fullSync;
    switch (item.syncType) {
      case 'manual':
        return isFullSync ? 'Full Sync' : 'Smart Sync';
      case 'auto':
        return 'Auto-sync';
      case 'webhook':
        return 'Webhook';
      case 'scheduled':
        return 'Scheduled';
      default:
        return item.syncType;
    }
  };

  const getSyncTypeBadgeColor = () => {
    const isFullSync = item.triggerMetadata?.fullSync;
    if (item.syncType === 'manual') {
      return isFullSync
        ? 'bg-orange-100 text-orange-700 ring-orange-200'
        : 'bg-blue-100 text-blue-700 ring-blue-200';
    }
    return 'bg-purple-100 text-purple-700 ring-purple-200';
  };

  const getDirectionLabel = () => {
    const labels: Record<string, string> = {
      to_xero: 'HL → Xero',
      from_xero: 'Xero → HL',
      bidirectional: 'Bidirectional',
    };
    return labels[item.direction] || item.direction;
  };

  const getEntityTypeLabels = () => {
    const labels: Record<string, string> = {
      contact: 'Contacts',
      product: 'Products',
      invoice: 'Invoices',
      contacts: 'Contacts',
      products: 'Products',
      invoices: 'Invoices',
    };
    return item.entityTypes.map((t) => labels[t] || t).join(', ');
  };

  const hasErrors = item.failedRecords > 0 || item.errorDetails?.length > 0;
  // Show details button for completed syncs or syncs with processed records
  const canViewDetails = ['completed', 'failed', 'partial_success'].includes(item.status) ||
    item.processedRecords > 0;

  return (
    <div
      className={cn(
        'rounded-lg border p-4 transition-all hover:shadow-sm',
        getStatusColor()
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          {getStatusIcon()}
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2">
              <p className="text-sm font-medium text-gray-900">
                {getEntityTypeLabels()}
              </p>
              <span className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
                getSyncTypeBadgeColor()
              )}>
                {getSyncTypeLabel()}
              </span>
              <span className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
                {getDirectionLabel()}
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {item.startedAt
                ? formatDistanceToNow(new Date(item.startedAt), {
                    addSuffix: true,
                  })
                : 'Not started'}
              {item.durationSeconds && (
                <span className="ml-2">
                  ({item.durationSeconds}s)
                </span>
              )}
            </p>
            {item.errorSummary && (
              <p className="mt-2 text-xs text-red-600 line-clamp-2">
                {item.errorSummary}
              </p>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="ml-4 flex flex-col items-end space-y-1">
          {item.totalRecords > 0 && (
            <div className="text-xs text-gray-600">
              <span className="font-medium">{item.successfulRecords}</span> / {item.totalRecords} synced
            </div>
          )}
          {item.skippedRecords > 0 && (
            <div className="text-xs text-blue-600">
              {item.skippedRecords} already synced
            </div>
          )}
          {item.failedRecords > 0 && (
            <div className="text-xs text-red-600">
              {item.failedRecords} failed
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center space-x-2 mt-2">
            {canViewDetails && (
              <button
                onClick={onViewDetails}
                className="inline-flex items-center text-xs text-gray-600 hover:text-gray-900"
                title="View sync details"
              >
                <Eye className="h-3.5 w-3.5 mr-1" />
                Details
              </button>
            )}
            {hasErrors && onRetry && (
              <button
                onClick={onRetry}
                className="inline-flex items-center text-xs text-blue-600 hover:text-blue-900"
                title="Retry failed items"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Retry
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar for in-progress syncs */}
      {item.status === 'in_progress' && item.totalRecords > 0 && (
        <div className="mt-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Progress</span>
            <span>{item.progressPercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-blue-600 h-1.5 rounded-full transition-all"
              style={{ width: `${item.progressPercentage}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Card for displaying a SyncJob (legacy format)
 */
function SyncJobCard({ job }: { job: SyncJob }) {
  const getStatusIcon = () => {
    switch (job.status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'running':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-gray-400" />;
      default:
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = () => {
    switch (job.status) {
      case 'completed':
        return 'bg-green-50 border-green-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      case 'running':
        return 'bg-blue-50 border-blue-200';
      case 'pending':
        return 'bg-gray-50 border-gray-200';
      default:
        return 'bg-yellow-50 border-yellow-200';
    }
  };

  const getEntityTypeLabel = () => {
    const labels = {
      contact: 'Contacts',
      product: 'Products',
      invoice: 'Invoices',
    };
    return labels[job.entityType] || job.entityType;
  };

  const getDirectionLabel = () => {
    const labels: Record<string, string> = {
      to_xero: 'HL → Xero',
      from_xero: 'Xero → HL',
      bidirectional: 'Bidirectional',
    };
    return labels[job.direction] || job.direction;
  };

  return (
    <div
      className={cn(
        'rounded-lg border p-4 transition-all hover:shadow-sm',
        getStatusColor()
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          {getStatusIcon()}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium text-gray-900">
                {getEntityTypeLabel()}
              </p>
              <span className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
                {getDirectionLabel()}
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {job.startedAt
                ? formatDistanceToNow(new Date(job.startedAt), {
                    addSuffix: true,
                  })
                : 'Not started'}
            </p>
            {job.error && (
              <p className="mt-2 text-xs text-red-600 line-clamp-2">
                {job.error}
              </p>
            )}
          </div>
        </div>
        {job.stats && (
          <div className="ml-4 flex flex-col items-end space-y-1">
            <div className="text-xs text-gray-600">
              <span className="font-medium">{job.stats.synced}</span> /{' '}
              {job.stats.total} synced
            </div>
            {job.stats.failed > 0 && (
              <div className="text-xs text-red-600">
                {job.stats.failed} failed
              </div>
            )}
            {job.stats.skipped > 0 && (
              <div className="text-xs text-yellow-600">
                {job.stats.skipped} skipped
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
